import { uiUrl } from "./portraits";

/**
 * The shop skin's painted chrome (ui/shopSkin.ts; art/interface/kit, generator tools/art/kit/gen_kit.py): engraved button plates, the
 * sheet's filigree frame, the plate's gold corner brackets, the well's iron bezel, the wax seal, the gold tag, the
 * cloth banners, the card plaques, the section lozenge and the vellum / leather surfaces. The CSS reads each one as
 * a custom property (`--kit-btn-gold` …, styles/shopskinKit.css) and falls back to plain gradients while it is unset, so the kit
 * works in tests and before boot. Set from here rather than written as url() in the CSS so a single-file build
 * (tools/web/singlefile.mjs) hands out its inlined data URLs like every other interface image.
 */
export const KIT_ART = {
  "btn-gold": "btn_gold.png",
  "btn-steel": "btn_steel.png",
  "btn-iron": "btn_iron.png",
  "btn-oxblood": "btn_oxblood.png",
  "btn-emerald": "btn_emerald.png",
  "frame-sheet": "frame_sheet.png",
  "frame-plate": "frame_plate.png",
  bezel: "bezel.png",
  seal: "seal_wax.png",
  tag: "tag_gold.png",
  "banner-navy": "banner_navy.png",
  "banner-red": "banner_red.png",
  "banner-steel": "banner_steel.png",
  "plaque-gold": "plaque_gold.png",
  "plaque-iron": "plaque_iron.png",
  lozenge: "lozenge.png",
  vellum: "vellum.webp",
  leather: "leather.webp",
} as const;

/** One kit piece's URL, absolute: a relative url() inside a custom property would resolve against the stylesheet
 * that reads it (assets/), not the page. Data URLs (a single-file build) pass through. */
export function kitArtUrl(file: string, base = typeof document === "undefined" ? "http://localhost/" : document.baseURI): string {
  const u = uiUrl(`kit/${file}`);
  return u.startsWith("data:") ? u : new URL(u, base).href;
}

/** The custom properties to set on :root: `--kit-<name>: url("…")`. */
export function kitArtVars(base?: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, file] of Object.entries(KIT_ART)) out[`--kit-${name}`] = `url("${kitArtUrl(file, base)}")`;
  return out;
}

/**
 * Sets the kit's custom properties on the document root and starts decoding the pieces, so the first sheet or button
 * paints with its plate. Resolves when they are decoded (or failed: the CSS falls back), for boot to await with the
 * fonts.
 */
export function installKitArt(root: HTMLElement = document.documentElement): Promise<void> {
  const vars = kitArtVars();
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
  const decode = Object.values(KIT_ART).map((file) => {
    const img = new Image();
    img.src = kitArtUrl(file);
    return img.decode().catch(() => undefined);
  });
  return Promise.all(decode).then(() => undefined);
}
